import { Icon, Text } from '@subframe/core';
import {
  useGetAccountStats,
  useListUpgradePlans,
  useListUpgradeTemplates,
} from 'api/frontend';
import {
  ResolvedEntitlement,
  ResolvedSubscription,
  SubscriptionPlan,
} from 'api/models';
import { ProductPlanContactUsButton } from 'components/ProductPlanContactUsButton';
import { ProductPlanDemo } from 'components/ProductPlanDemo';
import { RouterLink } from 'components/RouterLink';
import { AtomicTooltip, EntitlementBadge } from 'components/design-system';
import { endOfMonth, format, startOfMonth, subMonths } from 'date-fns';
import useAccountIdRoute from 'hooks/useAccountIdRoute';
import useUserAccountState from 'hooks/useUserAccountState';
import {
  Badge,
  ProductUsageItem,
  Progress,
  SkeletonText,
  SummaryBanner,
} from 'subframe/index';

import { fNumber } from 'utils/formatNumber';

import {
  getExpiryInDays,
  getExpiryInDaysText,
  getExpiryInText,
  getHumanReadableDate,
  getPlanExpiryState,
  getProgressValue,
  getUpgradeTemplatesUsage,
  isFirstMonthPassedSinceOnboarded,
} from 'utils/subscription_plan';
import { clusterFlattenPages, flattenPages } from 'utils/arrays';
import {
  useListClustersInfinite,
  useListUpgradeTemplatesInfinite,
} from 'api/frontend-infinite';
import { useStatsigClient } from '@statsig/react-bindings';
interface Props {
  currentSubscription: ResolvedSubscription | undefined;
  subscriptions: ResolvedSubscription[] | undefined;
  totalEntitlement: ResolvedEntitlement | undefined;
}

export const CurrentSubscription = ({
  currentSubscription,
  totalEntitlement,
  subscriptions,
}: Props) => {
  const expiryState = getPlanExpiryState(currentSubscription?.expiry);
  const { account } = useUserAccountState();

  const { data: clustersList } = useListClustersInfinite(
    '',
    {},
    {
      request: { headers: { Authorization: `Bearer ${account.token}` } },
    },
    {
      initialSize: 100,
      revalidateFirstPage: false,
    },
  );

  const listClustersResponse = clusterFlattenPages(clustersList || []) || [];

  const { data: accountStatsCount } = useGetAccountStats(
    'default',
    {
      view: 'count',
    },
    {
      request: { headers: { Authorization: `Bearer ${account.token}` } },
    },
  );

  // Upgrade Templates
  const { data: rawUpgradeTemplatesData, mutate } =
    useListUpgradeTemplatesInfinite(
      {},
      {
        request: {
          headers: { Authorization: `Bearer ${account.token}` },
        },
      },
    );
  const upgradeTemplatesData = flattenPages(rawUpgradeTemplatesData);

  const firstMonthPassed = isFirstMonthPassedSinceOnboarded(
    listClustersResponse || [],
  );

  const faqPath = useAccountIdRoute('/orgs/:orgId/subscriptions?view=faqs');

  const realClusterData = listClustersResponse
    ? listClustersResponse.filter(
        (c) =>
          c.internal_k8s_ref !== 'example-cluster-ref' && c.status === 'active',
      )
    : [];

  const isDemo = currentSubscription?.plan === 'Demo';

  const showUsage =
    !isDemo &&
    (totalEntitlement?.cluster_count?.limit ||
      totalEntitlement?.node_count?.limit ||
      totalEntitlement?.upgrade_plan_count?.limit);

  return (
    <div className="contents">
      <div className="w-full flex-col gap-[24px] items-start flex">
        <span className="text-default-font text-subheader">Plan Summary</span>
        {currentSubscription ? (
          <SummaryBanner
            className="max-w-[1400px]"
            items={
              <>
                <SummaryBanner.SummaryItem
                  name="Current Plan"
                  value={currentSubscription?.name || ''}
                  badge={null}
                />
                <SummaryBanner.SummaryItem
                  name="Started"
                  value={getHumanReadableDate(
                    currentSubscription?.started
                      ? currentSubscription?.started
                      : currentSubscription?.created,
                  )}
                  badge={null}
                />
                {currentSubscription?.expiry && (
                  <SummaryBanner.SummaryItem
                    name={getExpiryInText(currentSubscription?.plan)}
                    value={getHumanReadableDate(currentSubscription?.expiry)}
                    badge={
                      <>
                        {getExpiryInText(currentSubscription?.plan) !==
                          'Renews' && (
                          <>
                            {(expiryState === 'error' ||
                              expiryState === 'expired') && (
                              <Badge
                                variant="error"
                                icon="FeatherAlertTriangle"
                              >
                                {expiryState === 'error'
                                  ? `${getExpiryInDaysText(
                                      currentSubscription?.expiry,
                                    )}`
                                  : `${currentSubscription?.name} expired`}
                              </Badge>
                            )}

                            {expiryState === 'default' && (
                              <Badge variant="neutral">
                                {`${getExpiryInDays(
                                  currentSubscription?.expiry,
                                )}
                                days remaining`}
                              </Badge>
                            )}

                            {expiryState === 'warning' && (
                              <Badge
                                variant="warning"
                                icon="FeatherAlertTriangle"
                              >
                                {`${getExpiryInDays(
                                  currentSubscription?.expiry,
                                )} days remaining`}
                              </Badge>
                            )}
                          </>
                        )}
                      </>
                    }
                  />
                )}
                <SummaryBanner.SummaryItem
                  name="Activation Date"
                  value={getHumanReadableDate(currentSubscription?.created)}
                  badge={null}
                />
              </>
            }
          />
        ) : (
          <SkeletonText className="h-[88px]" />
        )}
      </div>
      <div />
      <div
        className="w-full flex-col gap-[24px] items-start flex"
        data-cy="plan-usage"
      >
        {totalEntitlement ? (
          <>
            {showUsage ? (
              <>
                <span className="text-default-font text-subheader">Usage</span>
                <div className="w-full gap-[16px] items-start flex flex-wrap">
                  {!!totalEntitlement?.node_count?.limit && (
                    <ProductUsageItem
                      name="Monthly Node Count"
                      headingSlot={
                        <div className="flex items-center justify-center gap-2">
                          <AtomicTooltip
                            tooltipContent={
                              <Text variant="label">
                                Average number of nodes across clusters from the
                                last monthly measurement cycle.{' '}
                                <>
                                  <RouterLink
                                    to={faqPath}
                                    className="hoverable-link"
                                  >
                                    FAQs
                                  </RouterLink>
                                </>{' '}
                                describe how average node count is computed.
                              </Text>
                            }
                          >
                            <Icon name="FeatherInfo" className="w-3" />
                          </AtomicTooltip>
                          <EntitlementBadge
                            consumptionAmount={
                              accountStatsCount?.average_nodes_count || 0
                            }
                            entitlementType={'node'}
                            entitlement={totalEntitlement}
                            showSubscriptionLink={false}
                          />
                        </div>
                      }
                      totalCount={totalEntitlement?.node_count?.limit?.toString()}
                      value={
                        !firstMonthPassed ? (
                          <AtomicTooltip
                            tooltipContent={
                              'The Monthly Node Count will be displayed at the beginning of the next month after a cluster is onboarded by installing Chkk K8s Connector.'
                            }
                          >
                            <span className="text-subtext-color text-body">
                              {'--'}
                            </span>
                          </AtomicTooltip>
                        ) : accountStatsCount !== undefined ? (
                          fNumber(accountStatsCount?.average_nodes_count ?? 0)
                        ) : (
                          <SkeletonText className="w-[50px] h-[38px]" />
                        )
                      }
                      progressBar={
                        <>
                          <Progress
                            value={
                              realClusterData
                                ? getProgressValue(
                                    accountStatsCount?.average_nodes_count || 0,
                                    totalEntitlement?.node_count?.limit || 0,
                                  )
                                : 0
                            }
                          />
                          <div className="flex items-end justify-center gap-1">
                            <span className="text-label font-label text-subtext-color">
                              Included in subscription:
                            </span>
                            <span className="text-label-bold font-label-bold text-subtext-color">
                              {fNumber(
                                totalEntitlement?.node_count?.limit ?? 0,
                              )}
                            </span>
                          </div>
                        </>
                      }
                    />
                  )}
                  {/* (!!totalEntitlement?.upgrade_template_count?.limit ||
                    !!totalEntitlement?.addon_upgrade_template_count?.limit) &&
                    currentSubscription &&
                    currentSubscription.plan != SubscriptionPlan.Enterprise && (
                      <ProductUsageItem
                        name="Upgrade Templates"
                        headingSlot={
                          <div className="flex items-center justify-center gap-2">
                            <AtomicTooltip
                              tooltipContent={
                                <Text variant="label">
                                  Upgrade Templates used vs included in
                                  subscription
                                </Text>
                              }
                            >
                              <Icon name="FeatherInfo" className="w-[14px]" />
                            </AtomicTooltip>
                            <EntitlementBadge
                              consumptionAmount={
                                rawUpgradeTemplatesData && subscriptions
                                  ? getUpgradeTemplatesUsage(
                                      upgradeTemplatesData || [],
                                      subscriptions,
                                    ).totalCount
                                  : 0
                              }
                              entitlementType={'upgrade_template'}
                              entitlement={totalEntitlement}
                              showSubscriptionLink={false}
                            />
                          </div>
                        }
                        totalCount={(
                          (totalEntitlement?.upgrade_template_count?.limit ||
                            0) +
                          (totalEntitlement?.addon_upgrade_template_count
                            ?.limit || 0)
                        )?.toString()}
                        value={
                          rawUpgradeTemplatesData && subscriptions ? (
                            getUpgradeTemplatesUsage(
                              upgradeTemplatesData || [],
                              subscriptions,
                            ).totalCount.toString()
                          ) : (
                            <SkeletonText className="w-[50px] h-[38px]" />
                          )
                        }
                        progressBar={
                          <>
                            <Progress
                              value={
                                rawUpgradeTemplatesData && subscriptions
                                  ? getProgressValue(
                                      getUpgradeTemplatesUsage(
                                        upgradeTemplatesData || [],
                                        subscriptions,
                                      ).totalCount,
                                      (totalEntitlement?.upgrade_template_count
                                        ?.limit || 0) +
                                        (totalEntitlement
                                          ?.addon_upgrade_template_count
                                          ?.limit || 0),
                                    )
                                  : 0
                              }
                            />
                            <div className="flex items-end justify-center gap-1">
                              <span className="text-label font-label text-subtext-color">
                                Included in subscription:
                              </span>
                              <span className="text-label-bold font-label-bold text-subtext-color">
                                {(
                                  (totalEntitlement?.upgrade_template_count
                                    ?.limit || 0) +
                                  (totalEntitlement
                                    ?.addon_upgrade_template_count?.limit || 0)
                                )?.toString()}
                              </span>
                            </div>
                          </>
                        }
                      />
                    )*/}
                </div>
              </>
            ) : (
              <></>
            )}
          </>
        ) : (
          <>
            <span className="text-default-font text-subheader">Usage</span>
            <SkeletonText className="h-[106px]" />
          </>
        )}
        <div className="flex-[1_0_0] h-full w-full gap-[8px] items-center flex">
          {!isDemo ? (
            <>
              <span className="text-default-font text-body-bold">
                To purchase additional Nodes or Upgrade Templates
              </span>
              <ProductPlanContactUsButton
                size="large"
                view={'Subscription Plan'}
                plan={'AdditionalQuota'}
              />
            </>
          ) : (
            <ProductPlanDemo
              view={'Subscription Plan'}
              plan={'AdditionalQuota'}
            />
          )}
        </div>
      </div>
    </div>
  );
};
